import { useEffect, useState } from "react";
import { useDocumentsData } from "../../data/use-documents-data";
import { useKitFoldersData } from "../../data/use-kit-folders-data";
import { Checkbox } from "../ui/checkbox";

export interface KitContentSelection {
  fileIds: string[];
  documentIds: string[];
}

interface KitContentSelectorProps {
  groupUuid: string | null;
  kitUuid: string | null;
  includeFiles?: boolean;
  includeDocuments?: boolean;
  onSelectionChange?: (selection: KitContentSelection) => void;
  selectedFiles?: string[];
  selectedDocuments?: string[];
}

export function KitContentSelector({
  groupUuid,
  kitUuid,
  includeFiles = true,
  includeDocuments = true,
  onSelectionChange,
  selectedFiles = [],
  selectedDocuments = [],
}: KitContentSelectorProps) {
  const [internalSelectedFiles, setInternalSelectedFiles] =
    useState<string[]>(selectedFiles);
  const [internalSelectedDocuments, setInternalSelectedDocuments] =
    useState<string[]>(selectedDocuments);

  const { folders } = useKitFoldersData(groupUuid, kitUuid);
  const { documents } = useDocumentsData(groupUuid, kitUuid);

  const projectFiles = folders.map((folder) => folder.files).flat();

  useEffect(() => {
    onSelectionChange?.({
      fileIds: internalSelectedFiles,
      documentIds: internalSelectedDocuments,
    });
  }, [internalSelectedFiles, internalSelectedDocuments, onSelectionChange]);

  const handleFileToggle = (fileId: string, checked: boolean) => {
    if (checked) {
      setInternalSelectedFiles([...internalSelectedFiles, fileId]);
    } else {
      setInternalSelectedFiles(
        internalSelectedFiles.filter((id) => id !== fileId),
      );
    }
  };

  const handleDocumentToggle = (documentId: string, checked: boolean) => {
    if (checked) {
      setInternalSelectedDocuments([...internalSelectedDocuments, documentId]);
    } else {
      setInternalSelectedDocuments(
        internalSelectedDocuments.filter((id) => id !== documentId),
      );
    }
  };

  const hasContent =
    (includeFiles && projectFiles.length > 0) ||
    (includeDocuments && documents.length > 0);

  if (!hasContent) {
    return null;
  }

  return (
    <div className="space-y-4">
      {includeFiles && projectFiles.length > 0 && (
        <>
          <div className="text-muted-foreground text-sm">
            Select files from the workbook to assist in generation:
          </div>
          <div className="grid max-h-60 grid-cols-2 gap-4 overflow-y-auto rounded border p-4">
            {projectFiles.map((file) => (
              <div key={file.uuid} className="flex items-center space-x-2">
                <Checkbox
                  id={`file-${file.uuid}`}
                  checked={internalSelectedFiles.includes(file.uuid)}
                  onCheckedChange={(checked) =>
                    handleFileToggle(file.uuid, !!checked)
                  }
                />
                <label
                  htmlFor={`file-${file.uuid}`}
                  className="cursor-pointer truncate text-sm leading-none font-medium"
                  title={file.name}
                >
                  {file.name}
                </label>
              </div>
            ))}
          </div>
        </>
      )}

      {includeDocuments && documents.length > 0 && (
        <>
          <div className="text-muted-foreground text-sm">
            Select documents from the workbook to assist in generation:
          </div>
          <div className="grid max-h-60 grid-cols-2 gap-4 overflow-y-auto rounded border p-4">
            {documents.map((document) => (
              <div key={document.uuid} className="flex items-center space-x-2">
                <Checkbox
                  id={`document-${document.uuid}`}
                  checked={internalSelectedDocuments.includes(document.uuid)}
                  onCheckedChange={(checked) =>
                    handleDocumentToggle(document.uuid, !!checked)
                  }
                />
                <label
                  htmlFor={`document-${document.uuid}`}
                  className="cursor-pointer truncate text-sm leading-none font-medium"
                  title={document.name}
                >
                  {document.name}
                </label>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
